import type { WaveEventV1 } from './WaveEventV1.js';

/**
 * A Song Pack v1 `.chart` document: `easy.chart`, `normal.chart` or `hard.chart`.
 *
 * Mirrors Unity's `SongPackFormat.ChartDocument`. The default values below are the
 * C# field initialisers from that class, so a document that omits an optional
 * top-level key is read exactly as Unity would read it.
 */

export const SONG_PACK_FORMAT_VERSION = 1;

export const DIFFICULTIES = ['easy', 'normal', 'hard'] as const;

export type Difficulty = (typeof DIFFICULTIES)[number];

export function isDifficulty(value: unknown): value is Difficulty {
  return typeof value === 'string' && (DIFFICULTIES as readonly string[]).includes(value);
}

export interface ChartDocumentV1 {
  readonly formatVersion: number;
  readonly difficulty: Difficulty;
  /** Song BPM recorded by the producer. */
  readonly bpm: number;
  /** BPM the analyser detected. May be 0 on hand-authored charts. */
  readonly detectedBpm: number;
  readonly firstBeatOffsetSeconds: number;
  /** Detected beat grid in seconds. May be empty on charts predating this field. */
  readonly detectedBeatTimes: readonly number[];
  readonly events: readonly WaveEventV1[];
  readonly poolCapacity: number;
  readonly positionRerollLimit: number;
  readonly minimumDistanceFromPreviousOrigin: number;
  readonly randomSeed: number;
}

/** Defaults for absent top-level keys, taken from Unity's `ChartDocument`. */
export const CHART_DOCUMENT_DEFAULTS = Object.freeze({
  bpm: 0,
  detectedBpm: 0,
  firstBeatOffsetSeconds: 0,
  poolCapacity: 16,
  positionRerollLimit: 12,
  minimumDistanceFromPreviousOrigin: 2,
  randomSeed: 1729,
});

/**
 * Upper bound on `events.length`, copied verbatim from Unity's
 * `SongPackService.ValidateChart`.
 */
export const MAX_CHART_EVENTS = 200000;

/** Canonical file name for a difficulty. Version 1 fixes these exactly. */
export function chartFileName(difficulty: Difficulty): string {
  return `${difficulty}.chart`;
}
